export type MixedAudioSession = {
  /** Single stream suitable for MediaRecorder */
  stream: MediaStream;
  micIncluded: boolean;
  micError?: string;
  dispose: () => void;
};

/**
 * Mix tab (or any MediaStream) audio with an optional microphone into one stream.
 */
export async function mixTabAndMicrophone(
  tabAudioStream: MediaStream,
  includeMicrophone: boolean,
): Promise<MixedAudioSession> {
  const audioContext = new AudioContext();
  const destination = audioContext.createMediaStreamDestination();
  const sourceNodes: MediaStreamAudioSourceNode[] = [];
  const ownedStreams: MediaStream[] = [];

  const tabSource = audioContext.createMediaStreamSource(tabAudioStream);
  tabSource.connect(destination);
  sourceNodes.push(tabSource);

  let micIncluded = false;
  let micError: string | undefined;

  if (includeMicrophone) {
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      ownedStreams.push(micStream);
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSource.connect(destination);
      sourceNodes.push(micSource);
      micIncluded = true;
    } catch (err) {
      micError =
        err instanceof DOMException && err.name === "NotReadableError"
          ? "Microphone is in use or unavailable. Recording tab audio only."
          : err instanceof DOMException && err.name === "NotAllowedError"
            ? "Microphone permission denied. Recording tab audio only."
            : "Could not use microphone. Recording tab audio only.";
    }
  }

  const dispose = () => {
    for (const node of sourceNodes) {
      try {
        node.disconnect();
      } catch {
        /* ignore */
      }
    }
    for (const stream of ownedStreams) {
      stream.getTracks().forEach((t) => t.stop());
    }
    void audioContext.close();
  };

  return {
    stream: destination.stream,
    micIncluded,
    micError,
    dispose,
  };
}